import React, { useContext, useEffect, useState } from 'react';
import { ErrorMessage, Field, Form, Formik, FormikErrors, FormikValues } from 'formik';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import * as Yup from 'yup';
import axios from 'axios';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TabPanel, TabView } from 'primereact/tabview';
import { InputNumber } from 'primereact/inputnumber';
import DateTextInput from 'app/component/date-text-input';

import 'primeflex/primeflex.css';
import { getCareersByType } from 'app/api/careerApi';
import { ToastContext } from 'app/context/ToastContext';
import { changePassword, getAgent, getCurrentStornofond } from 'app/api/agentApi';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { getAllAgentTypes } from 'app/api/agentTypeApi';
import AgentWidget from 'app/component/agent-widget';
import { getLast2Years } from 'app/api/agentPayedNettoProvisionApi';
import { useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants';
import AgentExamEducationPanel from 'app/main/agent/agent-exam-education-panel';
import AgentStornofondPanel from 'app/main/agent/agent-stornofond-panel'; // Define Yup validation schema

// Define Yup validation schema
const validationSchema = Yup.object({
  number: Yup.string().max(255, 'Max 255 znakov').required('Číslo je povinné'),
  name: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  firstName: Yup.string().max(255, 'Max 255 znakov').required('Meno je povinné'),
  lastName: Yup.string().max(255, 'Max 255 znakov').required('Priezvisko je povinné'),
  street: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  nrOrient: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  nrSupis: Yup.string().max(255, 'Max 255 znakov').required('Súpisné číslo je povinné.'),
  postalCode: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  city: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  country: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  phone: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  emailPrivate: Yup.string().max(255, 'Max 255 znakov').email('Nevalidný email').notRequired(),
  emailCompany: Yup.string().max(255, 'Max 255 znakov').email('Nevalidný email').notRequired(),
  registrationNr: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  birthNr: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  preukaz: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  titlePrefix: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  titlePostfix: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  ico: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  dic: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  iban: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  statFirstName: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  statLastName: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  statStreet: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  statStreetOrient: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  statStreetSupis: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  statCity: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  statPostcode: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  statCountry: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  statBirthNr: Yup.string().max(255, 'Max 255 znakov').notRequired(),
  statPreukaz: Yup.string().max(255, 'Max 255 znakov').notRequired(),
});

const subjectTypes = [
  { value: 1, label: 'Fyzická osoba' },
  { value: 2, label: 'Právnická osoba' },
];

// Component for creating a new Agent
const CreateAgent: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const account = useAppSelector(state => state.authentication.account);
  const isAdminOrAssistant = hasAnyAuthority(account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.SUPER_ADMIN, AUTHORITIES.ASSISTANT]);
  const isReadOnly = !isAdminOrAssistant || searchParams.has('info');
  const { id } = useParams<{ id?: string }>(); // Capture id from URL
  const [oldCareers, setOldCareers] = useState<any[]>([]);
  const [newCareers, setNewCareers] = useState<any[]>([]);
  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [agentTypes, setAgentTypes] = useState([]);
  const [loadingLast2Years, setLoadingLast2Years] = useState(true);
  const [provisionForLast2Years, setProvisionForLast2Years] = useState(0);
  const [currentStornofond, setCurrentStornofond] = useState(null);
  const [examEducations, setExamEducations] = useState([]);

  const loadCareers = () => {
    getCareersByType(1)
      .then(response => {
        setOldCareers(
          response.data.sort((a, b) => a.positionNr - b.positionNr).map(c => ({ value: c.id, label: c.positionNr + ' - ' + c.position })),
        );
      })
      .catch(error => {
        showError('Chyba', 'Nastala chyba pri nacitavani karierneho listu.');
      });

    getCareersByType(2)
      .then(response => {
        setNewCareers(
          response.data.sort((a, b) => a.positionNr - b.positionNr).map(c => ({ value: c.id, label: c.positionNr + ' - ' + c.position })),
        );
      })
      .catch(error => {
        showError('Chyba', 'Nastala chyba pri nacitavani karierneho listu.');
      });
  };

  useEffect(() => {
    getAllAgentTypes().then(data => {
      setAgentTypes(
        data.data.map(p => ({
          label: p.name,
          value: p.id,
        })),
      );
    });

    loadCareers();

    if (id) {
      getAgent(id)
        .then(response => {
          if (response.data) {
            setAgent(response.data);
            setExamEducations(response.data.examEducations);
          }
          setLoading(false);
        })
        .catch(error => {
          console.error(error);
          showError('Chyba', error.response?.data?.detail);
        });

      getCurrentStornofond(id)
        .then(response => {
          setCurrentStornofond(response.data);
        })
        .catch(e => {
          console.error(e);
          showError('Chyba', e.response?.data?.detail);
        });
    } else {
      setLoading(false);
    }

    if (id) {
      getLast2Years(id)
        .then(response => {
          setProvisionForLast2Years(response.data);
        })
        .catch(e => {
          console.error(e);
          showError('Chyba', 'Nastala chyba pri načítaní netto provízií za posledné 2 roky');
        })
        .finally(() => setLoadingLast2Years(false));
    } else {
      setLoadingLast2Years(false);
    }
  }, []);

  const handleEducationPanelChange = updatedEducations => {
    setExamEducations(updatedEducations);
    // You might want to update the 'agent' state here as well,
    // depending on how you manage your form data.
    // For example:
    // setAgent(prevAgent => ({ ...prevAgent, examEducations: updatedEducations }));
  };

  // Handle form submission
  const handleSubmit = (values: any) => {
    setIsSaving(true);

    let call;

    let isCreate = false;

    const tempValue = { ...values, examEducations };

    if (id) {
      call = axios.put(`/api/agents/${id}`, tempValue);
    } else {
      isCreate = true;
      call = axios.post('/api/agents', tempValue);
    }

    call
      .then(response => {
        showSuccess('Úspech', 'Poradca úspešne uložený.');
        setAgent(response.data);
        setExamEducations(response.data.examEducations);
        if (isCreate) {
          navigate(`/agents?p=${tempParameters}`);
        }
      })
      .catch(error => {
        showError('Chyba', error.response?.data?.detail);
        console.error('Error creating agent:', error);
      })
      .finally(() => setIsSaving(false));
  };

  function getBasicInfoTab(
    values: FormikValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
  ) {
    return (
      <TabPanel header="Základné info">
        <div className="grid">
          <div className="col-12">
            <div className="p-field">
              <label htmlFor="activeAgent">Aktívny agent</label>
              <Checkbox
                name="activeAgent"
                onChange={e => void setFieldValue('activeAgent', e.checked)}
                disabled={isReadOnly}
                checked={values.activeAgent}
              ></Checkbox>
              <ErrorMessage name="activeAgent" component="div" className="p-error" />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="number">Číslo*</label>
              <Field readOnly={isReadOnly} name="number" as={InputText} />
              <ErrorMessage name="number" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="registrationNr">Registračné číslo NBS</label>
              <Field readOnly={isReadOnly} name="registrationNr" as={InputText} />
              <ErrorMessage name="registrationNr" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="agentType">Typ poradcu</label>
              <Dropdown
                id="agentType"
                name="agentType"
                value={values.agentType?.id}
                options={agentTypes}
                optionLabel="label"
                disabled={isReadOnly}
                onChange={e => void setFieldValue('agentType.id', e.value)}
                placeholder="Vyberte si typ poradcu"
              />
              <ErrorMessage name="agentType" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="name">Meno</label>
              <Field readOnly={isReadOnly} name="name" as={InputText} />
              <ErrorMessage name="name" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="firstName">Prvé meno*</label>
              <Field readOnly={isReadOnly} name="firstName" as={InputText} />
              <ErrorMessage name="firstName" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="lastName">Priezvisko*</label>
              <Field readOnly={isReadOnly} name="lastName" as={InputText} />
              <ErrorMessage name="lastName" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="titlePrefix">Titul pred menom</label>
              <Field readOnly={isReadOnly} name="titlePrefix" as={InputText} />
              <ErrorMessage name="titlePrefix" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="titlePostfix">Titul za menom</label>
              <Field readOnly={isReadOnly} name="titlePostfix" as={InputText} />
              <ErrorMessage name="titlePostfix" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="dateOfBirth">Dátum narodenia</label>
              <DateTextInput
                id="dateOfBirth"
                readOnly={isReadOnly}
                value={values.dateOfBirth}
                onChange={val => void setFieldValue('dateOfBirth', val)}
              />
              <ErrorMessage name="dateOfBirth" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="birthNr">Rodné číslo</label>
              <Field readOnly={isReadOnly} name="birthNr" as={InputText} />
              <ErrorMessage name="birthNr" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="oldCareerId">Stará kariéra</label>
              <Dropdown
                id="oldCareerId"
                disabled={isReadOnly}
                name="oldCareerId"
                value={values.oldCareerId}
                options={oldCareers}
                optionLabel="label"
                onChange={e => void setFieldValue('oldCareerId', e.value)}
                placeholder="Vyberte kariéru"
              />
              <ErrorMessage name="oldCareerId" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="newCareerId">Nová kariéra</label>
              <Dropdown
                id="newCareerId"
                disabled={isReadOnly}
                name="newCareerId"
                value={values.newCareerId}
                options={newCareers}
                optionLabel="label"
                onChange={e => void setFieldValue('newCareerId', e.value)}
                placeholder="Vyberte kariéru"
              />
              <ErrorMessage name="newCareerId" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="preukaz">Preukaz</label>
              <Field readOnly={isReadOnly} name="preukaz" as={InputText} />
              <ErrorMessage name="preukaz" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12">
            <div className="p-field">
              <label htmlFor="description">Poznámka</label>
              <Field readOnly={isReadOnly} name="description" as={InputTextarea} autoResize />
              <ErrorMessage name="description" component="div" className="p-error" />
            </div>
          </div>
        </div>
      </TabPanel>
    );
  }

  function getAdressTab() {
    return (
      <TabPanel header="Adresa">
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="street">Ulica</label>
              <Field readOnly={isReadOnly} name="street" as={InputText} />
              <ErrorMessage name="street" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="nrSupis">Súpisné číslo *</label>
              <Field readOnly={isReadOnly} name="nrSupis" as={InputText} />
              <ErrorMessage name="nrSupis" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="nrOrient">Orientačné číslo</label>
              <Field readOnly={isReadOnly} name="nrOrient" as={InputText} />
              <ErrorMessage name="nrOrient" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="postalCode">PSČ</label>
              <Field readOnly={isReadOnly} name="postalCode" as={InputText} />
              <ErrorMessage name="postalCode" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="city">Obec</label>
              <Field readOnly={isReadOnly} name="city" as={InputText} />
              <ErrorMessage name="city" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="country">Štát</label>
              <Field readOnly={isReadOnly} name="country" as={InputText} />
              <ErrorMessage name="country" component="div" className="p-error" />
            </div>
          </div>
        </div>
      </TabPanel>
    );
  }

  function getContactInfoTab() {
    return (
      <TabPanel header="Kontaktné údaje">
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="phone">Tel. číslo</label>
              <Field readOnly={isReadOnly} name="phone" as={InputText} />
              <ErrorMessage name="phone" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="emailPrivate">Súkromný e-mail</label>
              <Field readOnly={isReadOnly} name="emailPrivate" as={InputText} />
              <ErrorMessage name="emailPrivate" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="emailCompany">Firemný e-mail</label>
              <Field readOnly={isReadOnly} name="emailCompany" as={InputText} />
              <ErrorMessage name="emailCompany" component="div" className="p-error" />
            </div>
          </div>
        </div>
      </TabPanel>
    );
  }

  function getAdditionalInfoTab(
    values: FormikValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
  ) {
    return (
      <TabPanel header="Ďalšie info">
        <div className="grid">
          <div className="col-12">
            <div className="p-field">
              <label htmlFor="superiorAgent">Nadradený agent</label>
              <AgentWidget
                id="superiorAgent"
                readonly={isReadOnly}
                value={values.superiorAgent?.id}
                onPersonChanged={e => {
                  void setFieldValue('superiorAgent.id', e);
                }}
                onCareerChanged={() => {}}
                showCareer={false}
              />
              <ErrorMessage name="superiorAgent" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="from">Od</label>
              <DateTextInput id="from" readOnly={isReadOnly} value={values.from} onChange={val => void setFieldValue('from', val)} />
              <ErrorMessage name="from" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="to">Do</label>
              <DateTextInput id="to" readOnly={isReadOnly} value={values.to} onChange={val => void setFieldValue('to', val)} />
              <ErrorMessage name="to" component="div" className="p-error" />
            </div>
          </div>
          {/*<div className="col-12 md:col-6">*/}
          {/*  <div className="p-field">*/}
          {/*    <label htmlFor="stornoMinimum">Storno Minimum</label>*/}
          {/*    <InputNumber*/}
          {/*      id="stornoMinimum"*/}
          {/*      value={values.stornoMinimum}*/}
          {/*      locale="sk"*/}
          {/*      minFractionDigits={0}*/}
          {/*      maxFractionDigits={10}*/}
          {/*      onValueChange={e => void setFieldValue('stornoMinimum', e.value)}*/}
          {/*    />*/}
          {/*    <ErrorMessage name="stornoMinimum" component="div" className="p-error" />*/}
          {/*  </div>*/}
          {/*</div>*/}
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="customStornofondValue">Vlastné % odvodu do stornofondu (prebije tabuľku)</label>
              <InputNumber
                id="customStornofondValue"
                readOnly={isReadOnly}
                value={values.customStornofondValue}
                locale="sk"
                minFractionDigits={0}
                maxFractionDigits={4}
                onValueChange={e => void setFieldValue('customStornofondValue', e.value)}
              />
              <ErrorMessage name="customStornofondValue" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="currentStornofondValue">Aktuálny stav stornofondu</label>
              <InputNumber
                id="currentStornofondValue"
                disabled={true}
                value={currentStornofond ?? 0}
                locale="sk"
                minFractionDigits={0}
                maxFractionDigits={2}
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="currentStornofondValue">Vyplatená netto provízia za posledné 2 roky</label>
              {loadingLast2Years ? (
                <div>
                  <ProgressSpinner />
                </div>
              ) : (
                <InputNumber
                  id="provisionForLast2Years"
                  value={provisionForLast2Years}
                  disabled={true}
                  locale="sk"
                  minFractionDigits={0}
                  maxFractionDigits={2}
                />
              )}
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="podpisDokumentovSoSpolocnostou">Dátum podpisu dokumentov so spoločnosťou</label>
              <DateTextInput
                id="podpisDokumentovSoSpolocnostou"
                readOnly={isReadOnly}
                value={values.podpisDokumentovSoSpolocnostou}
                onChange={val => void setFieldValue('podpisDokumentovSoSpolocnostou', val)}
              />
              <ErrorMessage name="podpisDokumentovSoSpolocnostou" component="div" className="p-error" />
            </div>
          </div>
        </div>
      </TabPanel>
    );
  }

  function getCompanyInfoTab(
    values: FormikValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
  ) {
    return (
      <TabPanel header="Údaje o firme">
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="subjectType">Typ subjektu</label>
              <Dropdown
                id="subjectType"
                name="subjectType"
                disabled={isReadOnly}
                value={values.subjectType}
                options={subjectTypes}
                optionLabel="label"
                onChange={e => {
                  void setFieldValue('subjectType', e.value);
                }}
                placeholder="Vyberte si typ subjektu"
              />
              <ErrorMessage name="subjectType" component="div" className="p-error" />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="ico">IČO</label>
              <Field readOnly={isReadOnly} name="ico" as={InputText} />
              <ErrorMessage name="ico" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="dic">DIČ</label>
              <Field readOnly={isReadOnly} name="dic" as={InputText} />
              <ErrorMessage name="dic" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="statFirstName">Štatutár - Meno</label>
              <Field readOnly={isReadOnly} name="statFirstName" as={InputText} />
              <ErrorMessage name="statFirstName" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="statLastName">Štatutár - Priezvisko</label>
              <Field readOnly={isReadOnly} name="statLastName" as={InputText} />
              <ErrorMessage name="statLastName" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="statStreet">Štatutár - Ulica</label>
              <Field readOnly={isReadOnly} name="statStreet" as={InputText} />
              <ErrorMessage name="statStreet" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="statStreetSupis">Štatutár - Súpisné číslo</label>
              <Field readOnly={isReadOnly} name="statStreetSupis" as={InputText} />
              <ErrorMessage name="statStreetSupis" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="statStreetOrient">Štatutár - Orientačné číslo</label>
              <Field readOnly={isReadOnly} name="statStreetOrient" as={InputText} />
              <ErrorMessage name="statStreetOrient" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="statCity">Štatutár - Obec</label>
              <Field readOnly={isReadOnly} name="statCity" as={InputText} />
              <ErrorMessage name="statCity" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="statPostcode">Štatutár - PSČ</label>
              <Field readOnly={isReadOnly} name="statPostcode" as={InputText} />
              <ErrorMessage name="statPostcode" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="statCountry">Štatutár - Štát</label>
              <Field readOnly={isReadOnly} name="statCountry" as={InputText} />
              <ErrorMessage name="statCountry" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="statBirthNr">Štatutár - Rodné číslo</label>
              <Field readOnly={isReadOnly} name="statBirthNr" as={InputText} />
              <ErrorMessage name="statBirthNr" component="div" className="p-error" />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="statPreukaz">Štatutár - Preukaz</label>
              <Field readOnly={isReadOnly} name="statPreukaz" as={InputText} />
              <ErrorMessage name="statPreukaz" component="div" className="p-error" />
            </div>
          </div>
        </div>
      </TabPanel>
    );
  }

  const onChangePassword = userAccount => {
    changePassword(userAccount)
      .then(response => {
        showSuccess('Úspech', 'Heslo a/alebo emailová adresa boli úspešne zmenené.');
      })
      .catch(error => {
        console.error(error);
        showError('Chyba', error.response?.data.detail);
      });
  };

  function getAccountTab(
    values: FormikValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
  ) {
    return (
      <TabPanel header="Účet">
        {values.userAccount?.id ? (
          <div className="grid">
            <div className="col-12 md:col-4">
              <div className="p-field">
                <label htmlFor="userAccount.email">E-mailova adresa účtu</label>
                <Field name="userAccount.email" as={InputText} />
              </div>
            </div>
            <div className="col-12 md:col-4">
              <div className="p-field">
                <label htmlFor="userAccount.password">Heslo</label>
                <Field readOnly={isReadOnly} type="password" name="userAccount.password" as={InputText} />
                <ErrorMessage name="userAccount.password" component="div" className="p-error" />
              </div>
            </div>
            <div className="col-12 md:col-4" style={{ marginTop: '20px' }}>
              <div className="p-field">
                <Button
                  disabled={isReadOnly}
                  type="button"
                  label="Zmeniť heslo / E-mail"
                  className="p-mt-3"
                  style={{ backgroundColor: '#e31818' }}
                  onClick={() => onChangePassword(values.userAccount)}
                />
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="grid">
              <div className="col-12">
                <div className="p-field">
                  <label htmlFor="userAccount.createAccount">Vytvoriť účet</label>
                  <Checkbox
                    name="userAccount.createAccount"
                    onChange={e => void setFieldValue('userAccount.createAccount', e.checked)}
                    checked={values.userAccount?.createAccount}
                  ></Checkbox>
                </div>
              </div>
              {values.userAccount?.createAccount && (
                <>
                  <div className="col-12 md:col-6">
                    <div className="p-field">
                      <label htmlFor="userAccount.email">E-mailova adresa účtu</label>
                      <Field name="userAccount.email" as={InputText} />
                      <ErrorMessage name="userAccount.email" component="div" className="p-error" />
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div className="p-field">
                      <label htmlFor="userAccount.password">Heslo</label>
                      <Field type="password" name="userAccount.password" as={InputText} />
                      <ErrorMessage name="userAccount.password" component="div" className="p-error" />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </TabPanel>
    );
  }

  function getBankTab() {
    return (
      <TabPanel header="Banka">
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="p-field">
              <label htmlFor="iban">IBAN</label>
              <Field readOnly={isReadOnly} name="iban" as={InputText} />
              <ErrorMessage name="iban" component="div" className="p-error" />
            </div>
          </div>
        </div>
      </TabPanel>
    );
  }

  function getExamEducationTab() {
    return (
      <TabPanel header="Skúšky a vzdelávanie">
        {/*<div className="grid">*/}
        <AgentExamEducationPanel agent={agent} onEducationChange={handleEducationPanelChange} existingEducations={examEducations} />
        {/*</div>*/}
      </TabPanel>
    );
    // return <AgentExamEducationPanel agent={agent} existingEducations={examEducations} onEducationChange={handleEducationPanelChange} />;
  }

  function getStornofondTab() {
    if (!id) return null;
    return (
      <TabPanel header="Stornofond">
        <AgentStornofondPanel agentId={id} />
      </TabPanel>
    );
  }

  return (
    <div className="card">
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}

      {!loading && (
        <>
          <h2>{id ? 'Upraviť dáta poradcu' : 'Vytvoriť nového poradcu'}</h2>
          <Formik
            initialValues={
              agent
                ? agent
                : {
                    activeAgent: '',
                    number: '',
                    name: '',
                    firstName: '',
                    lastName: '',
                    street: '',
                    nrOrient: '',
                    nrSupis: '',
                    postalCode: '',
                    city: '',
                    country: '',
                    phone: '',
                    emailPrivate: '',
                    emailCompany: '',
                    subjectType: '',
                    agentType: null,
                    superiorAgent: null,
                    from: '',
                    to: '',
                    registrationNr: '',
                    birthNr: '',
                    dateOfBirth: '',
                    preukaz: '',
                    titlePrefix: '',
                    titlePostfix: '',
                    ico: '',
                    dic: '',
                    iban: '',
                    statFirstName: '',
                    statLastName: '',
                    statStreet: '',
                    statStreetOrient: '',
                    statStreetSupis: '',
                    statCity: '',
                    statPostcode: '',
                    statCountry: '',
                    statBirthNr: '',
                    statPreukaz: '',
                    description: '',
                    customStornofondValue: null,
                    userAccount: null,
                  }
            }
            validationSchema={validationSchema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({ isSubmitting, setFieldValue, values, errors }) => {
              return (
                <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid">
                  <TabView>
                    {/* Basic Info Tab */}
                    {getBasicInfoTab(values, setFieldValue)}

                    {/* Address Tab */}
                    {getAdressTab()}

                    {/* Contact Info Tab */}
                    {getContactInfoTab()}

                    {/* Contact Info Tab */}
                    {getBankTab()}

                    {/* Additional Info Tab */}
                    {getAdditionalInfoTab(values, setFieldValue)}

                    {getExamEducationTab()}

                    {/* Additional Info Tab */}
                    {getCompanyInfoTab(values, setFieldValue)}

                    {getAccountTab(values, setFieldValue)}

                    {getStornofondTab()}
                  </TabView>

                  {/* Submit Button */}
                  {!isReadOnly && (
                    <Button
                      style={{ backgroundColor: '#e31818' }}
                      type="submit"
                      label="Uložiť"
                      icon="pi pi-check"
                      className="p-mt-3"
                      disabled={isSaving}
                    />
                  )}
                  <Button
                    type="button"
                    style={{ backgroundColor: 'gray', marginTop: '10px' }}
                    label="Naspäť"
                    icon="pi pi-arrow-left"
                    className="p-button-secondary"
                    onClick={() => navigate(`/agents?p=${tempParameters}`)}
                  />
                </Form>
              );
            }}
          </Formik>
        </>
      )}
    </div>
  );
};

export default CreateAgent;
